import { apiFetch } from '@shared/api/http'
import {
  CoproductionInviteSchema,
  CreateCoproductionInputSchema,
  UpdateCoproductionPercentageInputSchema,
  planCoproductionSync,
} from './coproduction.contract'
import type {
  CoproducerDraft,
  CoproductionInvite,
  CoproductionSyncAction,
  CreateCoproductionInput,
  UpdateCoproductionPercentageInput,
} from './coproduction.contract'

export async function createCoproduction(
  input: CreateCoproductionInput,
): Promise<CoproductionInvite> {
  const body = CreateCoproductionInputSchema.parse(input)
  const data = await apiFetch<unknown>('/partnerships/coproductions', { method: 'POST', body })
  return CoproductionInviteSchema.parse(data)
}

export async function updateCoproductionPercentage(
  id: string,
  input: UpdateCoproductionPercentageInput,
): Promise<CoproductionInvite> {
  const body = UpdateCoproductionPercentageInputSchema.parse(input)
  const data = await apiFetch<unknown>(`/partnerships/coproductions/${id}/percentage`, {
    method: 'PATCH',
    body,
  })
  return CoproductionInviteSchema.parse(data)
}

export async function removeCoproduction(id: string): Promise<CoproductionInvite> {
  const data = await apiFetch<unknown>(`/partnerships/coproductions/${id}`, { method: 'DELETE' })
  return CoproductionInviteSchema.parse(data)
}

export async function cancelCoproductionInvite(id: string): Promise<CoproductionInvite> {
  const data = await apiFetch<unknown>(`/partnerships/coproductions/${id}/invite`, {
    method: 'DELETE',
  })
  return CoproductionInviteSchema.parse(data)
}

export async function acceptCoproduction(token: string): Promise<CoproductionInvite> {
  const data = await apiFetch<unknown>(`/partnerships/coproductions/${token}/accept`, {
    method: 'POST',
  })
  return CoproductionInviteSchema.parse(data)
}

export async function rejectCoproduction(token: string): Promise<CoproductionInvite> {
  const data = await apiFetch<unknown>(`/partnerships/coproductions/${token}/reject`, {
    method: 'POST',
  })
  return CoproductionInviteSchema.parse(data)
}

export type CoproductionSyncResult =
  | { changed: false }
  | { changed: true; invite: CoproductionInvite | null }

export type CoproductionSyncStage = 'create' | 'update' | 'remove'

export class CoproductionSyncError extends Error {
  override readonly name = 'CoproductionSyncError'

  constructor(
    readonly operation: Exclude<CoproductionSyncAction['kind'], 'noop'>,
    readonly stage: CoproductionSyncStage,
    readonly previousRemoved: boolean,
    readonly underlyingError: unknown,
  ) {
    super('Nao foi possivel sincronizar a coproducao')
  }
}

async function runCoproductionStep<T>(
  operation: Exclude<CoproductionSyncAction['kind'], 'noop'>,
  stage: CoproductionSyncStage,
  task: () => Promise<T>,
  previousRemoved = false,
): Promise<T> {
  try {
    return await task()
  } catch (error) {
    throw new CoproductionSyncError(operation, stage, previousRemoved, error)
  }
}

export async function syncCoproduction(
  productId: string,
  next: CoproducerDraft | undefined,
  previous: CoproducerDraft | undefined,
): Promise<CoproductionSyncResult> {
  const action = planCoproductionSync(next, previous)

  switch (action.kind) {
    case 'noop':
      return { changed: false }
    case 'create':
      return {
        changed: true,
        invite: await runCoproductionStep('create', 'create', () =>
          createCoproduction({
            productId,
            coProducerEmail: action.coProducerEmail,
            percentage: action.percentage,
          }),
        ),
      }
    case 'update':
      return {
        changed: true,
        invite: await runCoproductionStep('update', 'update', () =>
          updateCoproductionPercentage(action.id, { percentage: action.percentage }),
        ),
      }
    case 'replace':
      await runCoproductionStep('replace', 'remove', () => removeCoproduction(action.removeId))
      return {
        changed: true,
        invite: await runCoproductionStep(
          'replace',
          'create',
          () =>
            createCoproduction({
              productId,
              coProducerEmail: action.coProducerEmail,
              percentage: action.percentage,
            }),
          true,
        ),
      }
    case 'remove':
      await runCoproductionStep('remove', 'remove', () => removeCoproduction(action.id))
      return { changed: true, invite: null }
  }
}
